import { useRequiredContext } from "@redotech/react-util/context";
import { BlockServiceTheme } from "@redotech/redo-block-service-common/messages";
import {
  cssFontFamily,
  customFontStylesToLoad,
} from "@redotech/redo-content-builder-common/brand-kit/font-utils";
import { useTranslation } from "@redotech/redo-i18n";
import { useMemo } from "react";
import { fontFaceCss } from "../../form-builder/inspectors/custom-fonts/hooks/custom-font-face-style";
import { TrackingPageContext } from "../context/tracking-page-context";

/**
 * The page's typography as CSS variables, the same for a Custom HTML block
 * however it renders: framed, inline on the shopper's page, or in the builder.
 */
export function useCustomHtmlTheme(): BlockServiceTheme | undefined {
  const { pageSettings, customFontFamilies } =
    useRequiredContext(TrackingPageContext);
  const { i18n } = useTranslation();
  const typography = pageSettings?.typography;
  return useMemo(
    () =>
      typography
        ? {
            lang: i18n.language,
            cssVars: {
              "--redo-body-font-family": cssFontFamily(
                typography.bodyFontFamily,
              ),
              "--redo-body-font-color": typography.bodyFontColor,
              "--redo-header-font-family": cssFontFamily(
                typography.headerFontFamily,
              ),
              "--redo-header-font-color": typography.headerFontColor,
            },
            fontFaces: fontFaceCss(
              customFontStylesToLoad(
                [typography.headerFontFamily, typography.bodyFontFamily],
                customFontFamilies,
              ),
            ),
          }
        : undefined,
    [customFontFamilies, i18n.language, typography],
  );
}

/**
 * Inline and builder-preview blocks read the theme under their own names; the
 * `--redo-` names are the block-service protocol, and lint reserves that
 * namespace for theme tokens.
 */
const LOCAL_VARIABLE_NAMES: Readonly<Record<string, string>> = {
  "--redo-body-font-family": "--custom-html-body-font-family",
  "--redo-body-font-color": "--custom-html-body-font-color",
  "--redo-header-font-family": "--custom-html-header-font-family",
  "--redo-header-font-color": "--custom-html-header-font-color",
};

/** The theme's variables under the names inline and builder-preview CSS read. */
export function localThemeVariables(
  theme: BlockServiceTheme | undefined,
): Record<string, string> | undefined {
  if (!theme?.cssVars) {
    return undefined;
  }
  return Object.fromEntries(
    Object.entries(theme.cssVars).map(([name, value]) => [
      LOCAL_VARIABLE_NAMES[name] ?? name,
      value,
    ]),
  );
}
